export interface RequestCodePayload {
  email: string;
}

export interface VerifyCodePayload {
  email: string;
  code: string;
}

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1') + '/auth/db';

// Safely parse a response without reading the stream twice
async function parseErrorBody(res: Response): Promise<{ raw: string; json?: any }> {
  try {
    const data = await res.clone().json();
    return { raw: JSON.stringify(data), json: data };
  } catch {
    try {
      const txt = await res.clone().text();
      return { raw: txt };
    } catch {
      return { raw: '' };
    }
  }
}

export async function requestCode(payload: RequestCodePayload): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE}/request-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const { json, raw } = await parseErrorBody(res);
    if (res.status === 404) {
      throw new Error('User not found. Please reach out to support@bayrocklabs.com to get an account.');
    }
    const msg = (json?.detail || json?.errors || json?.message || '').toString();
    throw new Error(msg || raw || 'Failed to request code');
  }
  const data = await res.json().catch(() => ({ data: { message: 'Code sent' } }));
  return { message: data?.data?.message || 'Code sent' };
}

export interface VerifyCodeResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: { id: string; email: string; role?: string };
}

export async function verifyCode(payload: VerifyCodePayload): Promise<VerifyCodeResponse> {
  const res = await fetch(`${API_BASE}/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Parse once safely and map to friendly message
    const { json, raw } = await parseErrorBody(res);
    let friendly = 'Invalid verification code or email. Please try again.';
    const rawMsg = (json?.detail || json?.message || json?.error?.message || '').toString();

    if (res.status === 400) {
      friendly = rawMsg || 'Invalid verification code or email. Please check and try again.';
    } else if (res.status === 401) {
      friendly = rawMsg || 'Your code has expired or is invalid. Please request a new code and try again.';
    } else if (res.status === 404) {
      friendly = rawMsg || 'Email not found. Please verify your email or contact support.';
    } else if (res.status === 429) {
      friendly = 'Too many attempts. Please wait a few minutes and try again.';
    } else if (res.status >= 500) {
      friendly = 'We’re having trouble verifying right now. Please try again shortly.';
    } else if (rawMsg) {
      friendly = rawMsg;
    } else if (raw && raw.length < 200 && raw.trim().startsWith('{') === false) {
      friendly = raw;
    }

    if (json?.correlation_id && process.env.NODE_ENV !== 'production') {
      console.debug('[verifyCode] correlation_id', json.correlation_id);
    }
    throw new Error(friendly);
  }
  return res.json();
}
